import { Component, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CustomerService } from '../../../customer/customer.service';
import { ToolbarService } from '../../../services/toolbar.service';
import { MessageService } from 'primeng/api';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-quick-contact-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule
  ],
  providers: [MessageService, CustomerService],
  template: `
    <div class="p-4">
      <form [formGroup]="contactForm" class="form-stack">
        <div class="form-grid">
          <div class="field col-span-2" [class.validation-field-invalid]="isInvalid('name')">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-user"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="newContactName" pInputText formControlName="name">
                <label for="newContactName">Contact Name *</label>
              </p-iftaLabel>
            </p-inputGroup>
            @if (isInvalid('name')) { <small class="p-error block mt-1">Contact name is required.</small> }
          </div>
          <div class="field col-span-2">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-envelope"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="newContactEmail" pInputText formControlName="emailAddress">
                <label for="newContactEmail">Email</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
          <div class="field col-span-2">
            <p-inputGroup>
              <p-inputGroupAddon><i class="pi pi-phone"></i></p-inputGroupAddon>
              <p-iftaLabel>
                <input id="newContactPhone" pInputText formControlName="phoneNumber">
                <label for="newContactPhone">Phone</label>
              </p-iftaLabel>
            </p-inputGroup>
          </div>
        </div>
      </form>
      <div class="flex justify-content-end gap-2 mt-3">
        <p-button label="Cancel" icon="pi pi-times" severity="secondary" [outlined]="true" (click)="close()"></p-button>
        <p-button label="Create Contact" icon="pi pi-check" [loading]="saving()" [disabled]="saving()" (click)="createContact()"></p-button>
      </div>
    </div>
  `,
  styles: [`
    :host ::ng-deep .p-inputgroup { width: 100%; }
    :host ::ng-deep .p-iftaLabel { flex: 1; }
    :host ::ng-deep .p-inputtext { width: 100%; }
  `]
})
export class QuickContactDialogComponent {
  private fb = inject(FormBuilder);
  private ref = inject(DynamicDialogRef);
  private config = inject(DynamicDialogConfig);
  private customerService = inject(CustomerService);
  private toolbarService = inject(ToolbarService);
  private messageService = inject(MessageService);

  saving = signal(false);

  contactForm = this.fb.group({
    name: ['', Validators.required],
    emailAddress: [''],
    phoneNumber: ['']
  });

  close() {
    this.ref.close();
  }

  createContact() {
    if (this.saving()) {
      return;
    }
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'Contact name is required.' });
      return;
    }
    const value = this.contactForm.value;
    const currentUser = this.toolbarService.customer();
    const customer = {
      name: (value.name || '').trim(),
      emailAddress: (value.emailAddress || '').trim(),
      phoneNumber: (value.phoneNumber || '').trim(),
      company: currentUser?.company,
      corporateRole: 'CLIENT_CONTACT',
      defaultLanguage: 'en',
      isPhoneVerified: false,
      rating: 0
    } as any;

    this.saving.set(true);
    this.customerService.createCustomer(customer)
      .pipe(finalize(() => this.saving.set(false)))
      .subscribe(response => {
        if (!response.success || !response.data) {
          this.messageService.add({ severity: 'error', summary: 'Error', detail: response.message || 'Failed to create contact.' });
          return;
        }
        this.ref.close(response.data);
      });
  }

  isInvalid(controlName: string) {
    const control = this.contactForm.get(controlName);
    return !!(control?.invalid && (control?.touched || control?.dirty));
  }
}
